/**
 * What's new — the release notes, newest first, shipped inside the build.
 *
 * **In the bundle, not on a server**, so a note arrives with the deploy it
 * describes and never before: a note served from elsewhere could announce a
 * feature the reader's build does not have yet. For the same reason the update
 * bar carries no "What's new" link — it runs in the *old* build, whose notes
 * stop before the change it is offering.
 *
 * **Written for the member, not the team** (the user guide's voice): what they
 * can now do and where, never a job, a backfill or a fix nobody saw. One entry
 * per deploy that a member can notice; added in the docs commit of that deploy.
 *
 * **Each item says who it is for.** `admin` items (work still under review,
 * like Macro) show to admins only, and `brands` limits an item to the brands
 * that have the feature. A day whose items are all filtered out is not shown.
 *
 * **Unread is per browser** — the newest id seen, in localStorage. The ☰
 * menu shows a dot until the panel is opened. Storage that throws (a private
 * window) reads as "seen", so nobody is nagged by a dot they cannot clear.
 */

export interface ReleaseItem {
  text: string;
  /** Where to find it, shown after the text — "Layouts › + Calendar". */
  where?: string;
  /** Admins only — work still under the owner's review. */
  audience?: "admin";
  /** Only these brands (`BRAND.key`); every brand when absent. */
  brands?: string[];
}

export interface ReleaseNote {
  /** Unique and increasing: the date, plus a suffix for a second deploy that day. */
  id: string;
  /** ISO date, shown as "Nov 2". */
  date: string;
  items: ReleaseItem[];
}

export const RELEASE_NOTES: ReleaseNote[] = [
  {
    id: "2026-11-02f",
    date: "2026-11-02",
    items: [
      {
        text: "Weekly or monthly indicators on a daily chart: set RSI to Weekly and each day shows the weekly RSI as it stood that day, with the week so far as the latest bar. It settles on the week's last day and never looks ahead.",
        where: "Indicators › ⚙ on the indicator › Timeframe",
      },
    ],
  },
  {
    id: "2026-11-02e",
    date: "2026-11-02",
    items: [
      {
        text: "Find the leaders on any screen: ERG Rank, which adds EPS Rank, RS Rank and group rank, sorts the strongest stocks into the growth boxes or a pie. Click a box to list its stocks, and pick any past date to see who led then and how far they've moved since.",
        where: "Screener bar › ▦ Boxes",
        audience: "admin",
      },
    ],
  },
  {
    id: "2026-11-02d",
    date: "2026-11-02",
    items: [
      {
        text: "The user guide now covers screening on seasonality: seasHit(30) >= 80 finds stocks up in 8 of the last 10 years over the next 30 days, and the AI can find it when you ask.",
        where: "Help › User guide › Screening on seasonality",
      },
    ],
  },
  {
    id: "2026-11-02c",
    date: "2026-11-02",
    items: [
      {
        text: "RSI shades above 70 and below 30. Any indicator's level can fill above or below it, in the colour and strength you choose. Levels are now dashed and easier to see.",
        where: "Indicators › ⚙ on the indicator › Levels › Fill",
      },
      {
        text: "Searching indicators also matches the category: type seas to list every Seasonality indicator.",
        where: "Indicators",
      },
    ],
  },
  {
    id: "2026-11-02",
    date: "2026-11-02",
    items: [
      {
        text: "The Calendar is open to everyone: economic releases and earnings, by week or month. Key releases sit first in each day, with the actual against the forecast.",
        where: "Layouts › + Calendar",
      },
      {
        text: "The AI has a new mark, in the search box, the chat and AI Review. It moves while the AI is thinking.",
      },
      {
        text: "Macro: an AI note on each section, and one across them all.",
        audience: "admin",
      },
    ],
  },
  {
    id: "2026-11-02b",
    date: "2026-11-02",
    items: [
      {
        text: "Macro is open to everyone: the economic releases, the yield curve, what moves together, safe havens, and positioning, each with a short AI note and one across them all at the top. Press ↻ on a note for a fresh one, once every 15 minutes.",
        where: "Layouts › + Macro",
      },
    ],
  },
  {
    id: "2026-11-03",
    date: "2026-11-03",
    items: [
      {
        text: "Calendar: Find a symbol's next earnings date, even when your filters hide it. Type the symbol and press Enter, then Show to open that week with it marked. Top cap now also offers 1000.",
        where: "Calendar › Earnings row › Find",
      },
    ],
  },
];

/** The notes this reader should see, newest first, empty days dropped. */
export function notesFor(brand: string, admin: boolean, notes: ReleaseNote[] = RELEASE_NOTES): ReleaseNote[] {
  return notes
    .map((note) => ({
      ...note,
      items: note.items.filter(
        (item) => (item.audience !== "admin" || admin) && (!item.brands || item.brands.includes(brand)),
      ),
    }))
    .filter((note) => note.items.length > 0)
    .sort((a, b) => (a.id < b.id ? 1 : a.id > b.id ? -1 : 0));
}

const SEEN_KEY = "qf.whatsNew.seen";

/** The newest note id this browser has opened, or null for never. */
export function lastSeen(): string | null {
  try {
    return localStorage.getItem(SEEN_KEY);
  } catch {
    return null;
  }
}

/** Whether any visible note is newer than the last one opened. */
export function hasUnread(visible: ReleaseNote[], seen: string | null | undefined = lastSeen()): boolean {
  if (visible.length === 0) return false;
  try {
    localStorage.getItem(SEEN_KEY);
  } catch {
    return false;
  }
  const newest = visible[0]!.id;
  return seen === null || seen === undefined || newest > seen;
}

/** Record the newest visible note as read. */
export function markSeen(visible: ReleaseNote[]): void {
  if (visible.length === 0) return;
  try {
    localStorage.setItem(SEEN_KEY, visible[0]!.id);
  } catch {
    /* a private window: the dot simply returns next visit */
  }
}

/** "Nov 2" from "2026-11-02", read as a calendar date, not a UTC instant. */
export function noteDate(iso: string): string {
  const [y = 1970, m = 1, d = 1] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
